import { createContext, useCallback, useContext, useMemo, useState } from "react";

/**
 * Holds the files the client picked in the Other Details panel until Book Trip creates the job and
 * uploads them. File objects are NOT redux-serializable, so they live here (in React state scoped to
 * the booking sheet) rather than in clientBookingSlice. Cleared after a successful booking.
 */
interface BookingAttachmentsValue {
  files: File[];
  addFiles: (files: File[]) => void;
  removeFile: (index: number) => void;
  /**
   * Edit flow: uuids of EXISTING (already-uploaded) attachments the user marked for removal. Staged
   * only — the actual DELETE fires when the user commits via "Update Booking" (submitUpdate), never on
   * toggle. Serialisable (just uuids), unlike the picked `File`s.
   */
  removedExistingIds: string[];
  /** Toggle an existing attachment's staged-for-removal state (edit flow). */
  toggleRemoveExisting: (uuid: string) => void;
  /** Un-stage all removals (e.g. when the user cancels a replacement upload → revert to the original). */
  clearRemovedExisting: () => void;
  /** Clear ALL staged attachment edits (picked files + staged removals) — on submit success / leave. */
  clearFiles: () => void;
}

const BookingAttachmentsContext = createContext<BookingAttachmentsValue | null>(null);

export const BookingAttachmentsProvider = ({ children }: { children: React.ReactNode }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [removedExistingIds, setRemovedExistingIds] = useState<string[]>([]);

  const addFiles = useCallback((incoming: File[]) => {
    if (incoming.length) setFiles((current) => [...current, ...incoming]);
  }, []);
  const removeFile = useCallback((index: number) => setFiles((current) => current.filter((_, i) => i !== index)), []);
  const toggleRemoveExisting = useCallback(
    (uuid: string) =>
      setRemovedExistingIds((current) => (current.includes(uuid) ? current.filter((id) => id !== uuid) : [...current, uuid])),
    []
  );
  const clearRemovedExisting = useCallback(() => setRemovedExistingIds([]), []);
  const clearFiles = useCallback(() => {
    setFiles([]);
    setRemovedExistingIds([]);
  }, []);

  const value = useMemo(
    () => ({ files, addFiles, removeFile, removedExistingIds, toggleRemoveExisting, clearRemovedExisting, clearFiles }),
    [files, addFiles, removeFile, removedExistingIds, toggleRemoveExisting, clearRemovedExisting, clearFiles]
  );

  return <BookingAttachmentsContext.Provider value={value}>{children}</BookingAttachmentsContext.Provider>;
};

/**
 * Access the booking-sheet attachment store. Returns a no-op fallback when used outside the provider
 * so the panels/summary render safely in isolation (e.g. Storybook, unit tests).
 */
export const useBookingAttachments = (): BookingAttachmentsValue => {
  const ctx = useContext(BookingAttachmentsContext);
  if (ctx) return ctx;
  return {
    files: [],
    addFiles: () => undefined,
    removeFile: () => undefined,
    removedExistingIds: [],
    toggleRemoveExisting: () => undefined,
    clearRemovedExisting: () => undefined,
    clearFiles: () => undefined,
  };
};
